import React, { useEffect, useState } from "react";
import { useTreeContext } from "../../context/TreeDataContext";
import { usePublicScreenContext } from "../../context/PublicScreenContext";
import { useModalContext } from "../../context/ModalContext";
import { useAxiosContext } from "../../context/AxiosContext";
import { useNavigationContext } from "../../context/NavigationContext";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import fileFolderConfig from "../../commonFunctions/fileFolderConfig";
import Navbar from "../../Layouts/Navbar/Navbar";
import LeftSideBar, { FolderCollapse } from "../../components/SideBar/LeftSideBar";
import { TryAgainMessage } from "../Screen/ScreenSection";
import Input from "../../components/FormComponents/Input";
import Button from "../../components/FormComponents/Button";
import ResizableComponent from "../../Layouts/ResizableComponent";
import HeaderModes from "../../Layouts/Navbar/HeaderModes";
import { toastError } from "../../utils/toastUtils";
import { deleteFolderOfFile, editFolderOrFile, insertNewFileOrFolder } from "../../utils/treeData/folderEditorUtils";
import BuilderModal from "../../Layouts/Modal/BuilderModal";

const PublicScreenSection = () => {
  const { publicAccessExplorerData, setPublicAccessExplorerData } = useTreeContext();
  const { selectedPublicScreen, fetchAllPublicScreens, setSelectedPublicScreen } = usePublicScreenContext();

  const { getUserConfirmationForDelete } = useModalContext();
  const { axiosInstance, handleError, handleSuccess } = useAxiosContext();

  const [isLoader, setIsLoader] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { selectedDevice } = useTreeContext();
  const { saveChangesAndNavigate } = useNavigationContext();
  const [isError, setIsError] = useState(false);

  const navigate = useNavigate();
  const { publicId } = useParams();

  const handleNewInsert = async (folderId, name) => {
    let config;
    try {
      const trimName = name.trim();
      if (trimName.toLowerCase() === "header" || trimName.toLowerCase() === "home") {
        toastError("Name 'header' or 'home' is not allowed.");
        return;
      }
      if (folderId.name === "publicform") {
        config = {
          name: name,
          fields: [],
          submit_button: "",
          custom_js: ""
        };
      } else {
        config = {
          name: name,
          device: folderId.name,
          config: {},
          description: ""
        };
      }
      const endpoint = folderId.name === "publicform" ? "public-screen-form" : "public-screens";
      const { data, message } = await axiosInstance.post(`/${endpoint}`, config);
      const newItem = fileFolderConfig.getFileConfig(data.id, data.name, folderId.name); // new item alwas delete and editable

      const newTree = insertNewFileOrFolder(publicAccessExplorerData, folderId, newItem);
      setPublicAccessExplorerData(newTree);
      navigate(`./${data.id}`);
      handleSuccess(message);
    } catch (error) {
      handleError(error);
    }
  };

  const handleDelete = async (folder) => {
    try {
      const userConfirmed = await getUserConfirmationForDelete(folder.name);
      if (!userConfirmed) return;
      const endpoint = folder.parentData === "publicform" ? "public-screen-form" : "public-screens";

      const { message } = await axiosInstance.delete(`/${endpoint}/${folder.id}`);
      const newTree = deleteFolderOfFile(publicAccessExplorerData, folder);
      setPublicAccessExplorerData([...newTree]);
      if (folder.id === selectedPublicScreen?.id) {
        navigate("./");
      }
      handleSuccess(message);
    } catch (error) {
      handleError(error);
    }
  };

  const handleEdit = async (folder, newName) => {
    try {
      const config = {
        name: newName
      };
      const endpoint = folder.parentData === "publicform" ? "public-screen-form" : "public-screens";

      const { message } = await axiosInstance.patch(`/${endpoint}/${folder.id}`, config);
      const newTree = editFolderOrFile(publicAccessExplorerData, folder, newName);
      setPublicAccessExplorerData([...newTree]);
      handleSuccess(message);
    } catch (error) {
      handleError(error);
    }
  };

  const callbackFunction = (id) => {
    if (selectedPublicScreen.id === id) return;

    saveChangesAndNavigate(id);
  };

  const initialFetching = async () => {
    setIsLoader(true);
    setIsError(false);
    try {
      if (publicAccessExplorerData && publicAccessExplorerData.length > 0) {
        setPublicAccessExplorerData(publicAccessExplorerData);
        if (selectedPublicScreen.id && !publicId) {
          setSelectedPublicScreen({ id: "" });
        }
      } else {
        await fetchAllPublicScreens();
      }
    } catch (error) {
      handleError(error);
      setIsError(true);
    } finally {
      setIsLoader(false);
    }
  };

  useEffect(() => {
    initialFetching();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!publicAccessExplorerData) return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevice, publicAccessExplorerData]);

  const createScreens = async (name) => {
    try {
      const screenConfig = {
        name: name,
        device: selectedDevice === 0 ? "desktop" : "mobile",
        config: {},
        description: "string"
      };

      const { data, message } = await axiosInstance.post(`/public-screens`, screenConfig);
      const newItem = fileFolderConfig.getFileConfig(data.id, data.name, selectedDevice === 0 ? "desktop" : "mobile");
      setPublicAccessExplorerData((prevData) => {
        const updatedData = [...prevData];
        updatedData[selectedDevice].items = [...updatedData[selectedDevice].items, newItem];
        return updatedData;
      });
      navigate(`./${data.id}`);
      handleSuccess(message);
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <>
      <section className="main_section">
        <div className="sub_section">
          {isError ? (
            <TryAgainMessage retryFunction={initialFetching} />
          ) : (
            <>
              <ResizableComponent
                resizeSide="right"
                storageKey="left_sidebar_size"
                defaultSize={244}
                range={[244, 400]} // Specify the range as [minSize, maxSize]
                collapsible={true}
              >
                <HeaderModes />
                <div className="d_flex">
                  <Navbar />
                  <LeftSideBar
                    explorerData={publicAccessExplorerData}
                    setExplorerData={setPublicAccessExplorerData}
                    selectedScreen={selectedPublicScreen}
                    callbackFunction={callbackFunction}
                    handleNewInsert={handleNewInsert}
                    handleDelete={handleDelete}
                    handleEdit={handleEdit}
                    type="Public Access"
                    isLoader={isLoader}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <FolderCollapse
                        explorerData={publicAccessExplorerData}
                        setExplorerData={setPublicAccessExplorerData}
                        type="public_screen"
                      />
                    </div>
                  </LeftSideBar>
                </div>
              </ResizableComponent>
              <Outlet />
            </>
          )}
        </div>
      </section>
      <BuilderModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} modalName="Create Screen">
        <CreatePublicScreensModal createScreens={createScreens} setIsModalOpen={setIsModalOpen} />
      </BuilderModal>
    </>
  );
};

const CreatePublicScreensModal = ({ createScreens, setIsModalOpen }) => {
  const [publicScreenName, setPublicScreenName] = useState("");

  return (
    <div>
      <div className="builder_form">
        <Input
          label="Screen Name"
          placeholder="Enter Public Screen Name"
          name="firstName"
          value={publicScreenName}
          onChange={(e) => setPublicScreenName(e.target.value)}
        />
      </div>
      <Button
        onClick={() => {
          setIsModalOpen(false);
          createScreens(publicScreenName);
        }}
      >
        Create
      </Button>
    </div>
  );
};

export default PublicScreenSection;
